// Page tagging for the inherited Stellantis "Work Ready" SiteBuilder page
// (/work-ready.htm). Alias: SITEBUILDER_WORK_READY_1 (F297885).
// Spec: "Stellantis Adobe Analytics - BusinessLink | Dealer Site Page", SDG
// 5.4.2.5 (Click to Call) and 5.4.2.7 (Social Interaction).

import {
	getGranularClickToCallAttributes,
	getSocialNetwork,
	toAdobeLinkNameSlug
} from '../../../../utils/stellantis/utils';

// CMS-authored, one per widget. Neither rule fires until they exist; the phone
// class also needs `widgetClasses-mobile`, as an empty -mobile variant overrides
// the base and the phones are mobile-only.
const PHONE_TAGGING_CLASS = 'tagging-contact-us-today-phone';
const SOCIAL_TAGGING_CLASS = 'tagging-contact-us-today-social';
const INVENTORY_TAGGING_CLASS = 'tagging-view-commercial-inventory';

// Hardcoded: "CONTACT US TODAY" is authored in a different widget, so
// eventData.widgetHeading never carries it.
const COMPONENT_TITLE = 'contact-us-today';

// widgetClasses is what ws-tracking publishes for ddc.link.generic. The DOM
// fallback is for socialHeaderClick, which carries no widget metadata at all.
const inTaggedWidget =
	(taggingClass) =>
	({ eventData, element } = {}) =>
		`${eventData?.widgetClasses || ''}`.includes(taggingClass) ||
		Boolean(element?.closest?.(`.${taggingClass}`));

// The department label, lower-cased with whitespace REMOVED rather than
// hyphenated, per the spec's `sales:…` prefix. A dealer-authored label of
// "Sales Test" therefore reports `salestest`, which is what NP validated.
//
// Deliberately not default.js's toPhoneSlug for this half: it only drops
// whitespace ADJACENT to a digit, so the space after "Sales:" — sitting between
// a colon and a digit-less character — would come out `sales:-419-210-8029`.
const squash = (value = '') =>
	`${value}`.trim().toLowerCase().replace(/\s+/g, '');

// The SDG wants hyphen-delimited digit groups. Rejoining the digit runs handles
// every form these rows render; stripping whitespace alone leaves "(419)210-8029".
const toSpecPhone = (value = '') => (`${value}`.match(/\d+/g) || []).join('-');

// Read from the DOM because it is not on the event — contact-info puts it in a
// span sibling to the anchor. `.value` beats the href because DDC's phone
// service rewrites it when call tracking swaps the number.
const getPhoneNumber = (element) => {
	const row = element?.closest?.('[data-click-to-call]');

	if (!row) {
		return undefined;
	}

	const [attributeNumber] = `${
		row.getAttribute('data-click-to-call-phone') || ''
	}`.split('?');

	return (
		toSpecPhone(
			row.querySelector?.('.value')?.textContent || attributeNumber
		) || undefined
	);
};

// <component text> for a Vehicle Selection tile, from the tile's own href
// (?search=1500&make=Ram -> ram-1500): eventData.text is the image src, not the
// model. URLSearchParams because '+' must decode to a space ("ProMaster+City").
//
// undefined unless BOTH params are present, so the rule can use it as its own
// guard: a non-tile link in the same widget falls through to the sitewide rule
// rather than reporting an empty or half-built description.
const makeModelSlug = (url = '') => {
	const params = new URLSearchParams(
		String(url).split('?').slice(1).join('?')
	);
	const make = params.get('make');
	const model = params.get('search');

	if (!make || !model) {
		return undefined;
	}

	return [make, model].map(toAdobeLinkNameSlug).filter(Boolean).join('-');
};

// ── "Let's Work Together" component (SDG click to call, US2102108) ─────────
// A separate page-section further down the same page, reported as type
// 'feature' / location 2 rather than 'dealer-information' / 1, so it needs its
// own rule rather than a branch in the ones above.
//
// Scoped by WIDGET NAME, not by a `tagging-` class, because the phone is not in
// the widget that carries the heading. The section is two sibling widgets:
//   content7       | content-default  -> "LET'S WORK TOGETHER!" heading only
//   staff-listing1 | ws-staff-listing -> the staff cards, and the phones
// so a class on the heading widget would never see these clicks. ws-tracking
// publishes widgetName for OSIRIS widgets and 'ws-staff-listing' is specific
// enough alone, which also means this rule carries NO pending CMS dependency.
//
// ASSUMPTION: one staff-listing widget on this page, the one in this component.
// A second one elsewhere would report with this component's taxonomy; scope by
// widgetId if that happens.
const WORK_TOGETHER_WIDGET_NAME = 'ws-staff-listing';

// <component title> for the rule below. Double-quoted because the client's
// component name carries an apostrophe, which eVar75 takes verbatim:
// dealer|feature|2|let's-work-together!|480-424-3593. Hardcoded for the same
// reason as COMPONENT_TITLE above, and it could not come from
// eventData.widgetHeading anyway — for these clicks that is the staff-listing
// widget's own "Commercial Vehicle Sales".
//
// The trailing "!" is part of the name, not decoration: the client's row is
// `let's-work-together!`, matching the "LET'S WORK TOGETHER!" heading the
// content-default widget authors above the cards. It was dropped in the first
// build, so eVar75 reported one segment short of the spec.
const WORK_TOGETHER_COMPONENT_TITLE = "let's-work-together!";

// A phone number anywhere inside a string, with an optional country code:
// "Call (480) 424-3593 today" -> "(480) 424-3593". The gate that makes the
// reads below safe — a label containing no number simply cannot win.
const PHONE_PATTERN = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;

// The number as the SDG reports it for the Let's Work Together component:
// `area-prefix-line`, e.g. `480-424-3593` / `281-651-3609`. No parentheses:
// the client's row is
//   dealer|feature|2|let's-work-together!|480-424-3593
// Earlier builds wrapped the area code (`(480)424-3593`, then `(480)-424-3593`);
// both were wrong.
//
// NORMALISED onto that shape, not passed through. The dealer's typing is not
// the dimension: the CMS authors this field freely — "(480) 424-3593" on the
// spec's example dealer, "281-651-3609" live on
// autonation-chrysler-dodge-jeep-ram-spring — and every form has to collapse
// onto the one reported shape.
//
// This now agrees with toSpecPhone's FORMAT, but the two are still separate
// because they take different inputs. toSpecPhone naively joins the digit runs
// of an already-clean DOM string; this has to pick a number out of arbitrary
// label text (hence the PHONE_PATTERN gate — see
// getWorkTogetherComponentText), drop a leading country code, and regroup a
// run-together "4804243593", which toSpecPhone would emit ungrouped.
//
// The two eVar75 rows on this page still differ, just by the department prefix
// rather than the number shape:
//   dealer|dealer-information|1|contact-us-today|sales:408-424-3593
//   dealer|feature|2|let's-work-together!|480-424-3593
//
// The length branch is belt-and-braces: PHONE_PATTERN already mandates exactly
// ten digits plus an optional leading 1, so the fallback is unreachable today.
// It routes through toSpecPhone rather than squash so that loosening the
// pattern later cannot reintroduce the parentheses this row must not carry.
const toAuthoredPhone = (value) => {
	const [match] = `${value ?? ''}`.match(PHONE_PATTERN) || [];

	if (!match) {
		return undefined;
	}

	const digits = match.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

	return digits.length === 10
		? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
		: toSpecPhone(match);
};

const stripTel = (value) =>
	`${value || ''}`.replace(/^tel:/i, '').split('?')[0];

// <component text> for the Let's Work Together click-to-call: the number alone,
// with no department prefix — a staff card has no department label, which is
// also why eVar29 reads `other` for these clicks.
//
// LIVE REGRESSION this exists to fix (NP, autonation-chrysler-dodge-jeep-ram-
// spring): eVar75 read `dealer|feature|2|let's-work-together|phone-number`.
// This rule had delegated `description` to default.js's
// getClickToCallDescription, whose chain takes the anchor's VISIBLE text first
// and only falls through to the tel: href when that is empty. DDC reports
// "Phone Number" as the visible text of this icon anchor — ws-staff-listing
// renders the call action as <a href="tel:…"><i class="ddc-icon ddc-icon-call"/></a>
// with no text node, so DDC falls back to its aria-label/title — so the chain
// returned that label slugged and never reached its own href fallback.
//
// Every source here is therefore gated on PHONE_PATTERN, and the DOM href is
// preferred over any text: ws-staff-listing renders it as
// `tel:${staffMember.phone}`, the same value it prints on the card, so it is
// both the most reliable source and immune to whatever label DDC attaches.
//
// NOTE this deliberately diverges from the shared helper for the in-modal Call
// button too: that anchor does have a real label, which default.js would keep
// (as it does for the header phone's `call`), but this component's client-
// confirmed rows are numbers, so the number wins here.
const getWorkTogetherComponentText = ({ element, text, url } = {}) => {
	const anchor = element?.closest?.('a');

	return (
		toAuthoredPhone(stripTel(anchor?.getAttribute?.('href'))) ||
		toAuthoredPhone(anchor?.textContent) ||
		toAuthoredPhone(text) ||
		// eventData.url last, for a click where `element` never reached us.
		toAuthoredPhone(stripTel(url))
	);
};

// eVar29 (clickToCallDepartment) for these clicks. The shared helper resolves
// it from eventData.departmentId / department, and DDC publishes NEITHER for a
// staff-card call icon — its href carries no ?dept= query, unlike the
// contact-info rows Contact Us Today reads — so the live beacon read `other`.
//
// ws-staff-listing does render the department, just never on the event, so read
// it from the widget's own copy. Sources, most specific first:
//   1. the card's job title, the <p> beside the name ("Fleet Sales",
//      "Commercial Sales Manager")
//   2. the DeptHeading <h2> the card sits under ("Commercial Vehicle Sales")
//   3. eventData.widgetHeading, if the DOM never reached us
//
// Lower-cased with whitespace REMOVED, the same shape this file already uses
// for the eVar75 department prefix (see squash), so "Fleet Sales" reports
// `fleetsales`. Client-confirmed: they want each card's real department, not a
// single value for the component.
//
// This OVERRIDES the helper's clickToCallDepartment rather than feeding it a
// departmentId, and the difference is the whole point: the helper validates
// against ['Sales','Service','Parts'] and collapses everything else to
// 'Other', which cannot express "fleetsales". HEADS UP for whoever maintains
// the Adobe side — eVar29 now carries values outside that three-word
// vocabulary for this component only; if a classification or report depends on
// it, this is the rule to look at.
const closestStaffCard = (element, widget) => {
	let node = element;

	while (node && node !== widget) {
		if (node.querySelector?.('h3')) {
			return node;
		}

		node = node.parentElement;
	}

	return undefined;
};

const getWorkTogetherDepartment = ({ element, widgetHeading } = {}) => {
	const anchor = element?.closest?.('a') || element;
	const widget = anchor?.closest?.('[data-widget-name]');
	const card = closestStaffCard(anchor, widget);

	// Details.jsx renders the job title and the phone number as SIBLING <p>s,
	// so skip anything holding a number: a card with no title would otherwise
	// report the phone number as its department.
	const jobTitle = [...(card?.querySelectorAll?.('p') || [])]
		.map((node) => `${node.textContent || ''}`.trim())
		.find((value) => value && !PHONE_PATTERN.test(value));

	// The card's own department group when the widget renders several, else the
	// widget's heading.
	const heading = (
		card?.closest?.('.container-fluid') || widget
	)?.querySelector?.('h2')?.textContent;

	return squash(jobTitle || heading || widgetHeading) || undefined;
};

export default {
	events: {
		// Click to Call (SDG 5.4.2.5).
		// eVar75 = dealer|dealer-information|1|contact-us-today|sales:419-476-0761
		//
		// Unshifted because these anchors carry `btn`, so default.js's legacy
		// CTA rule would otherwise claim them. Not flag-gated:
		// ws-tagging-stellantis-adobe-updates is off on the client's accounts.
		'ddc.link.generic': [
			{
				insertion: 'unshift',
				logic: (args = {}) =>
					args?.eventData?.isTelephonicUrl === true &&
					inTaggedWidget(PHONE_TAGGING_CLASS)(args),
				payload: ({ eventData = {}, element } = {}) => {
					const attributes =
						getGranularClickToCallAttributes(eventData);
					const number = getPhoneNumber(element);

					return {
						type: 'CustomTagEvent',
						eventName: 'clickToCall',
						eventAction: 'pageElementInteraction',
						attributes: {
							...attributes,
							type: 'dealer-information',
							title: COMPONENT_TITLE,
							// Prefix is the resolved department, not the
							// row's `.type` label: eVar29 has a fixed
							// vocabulary and a dealer-authored label would
							// not aggregate. Falls back to the helper's
							// description when there is no number.
							...(number && {
								description: `${attributes.clickToCallDepartment.toLowerCase()}:${number}`
							})
						}
					};
				}
			},
			{
				insertion: 'unshift',
				logic: ({
					eventData: { widgetClasses = '', url = '' } = {}
				} = {}) =>
					widgetClasses.includes(INVENTORY_TAGGING_CLASS) &&
					Boolean(makeModelSlug(url)),
				payload: ({ eventData: { url = '' } = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						site: 'dealer',
						type: 'vehicle-selection',
						location: 1,
						title: 'view-commercial-inventory',
						description: makeModelSlug(url)
					}
				})
			},
			// Click to Call Event (BusinessLink SDG 5.4.2.5) for the Let's Work
			// Together phones — the ws-staff-listing call icons. eVar75 =
			// dealer|feature|2|let's-work-together!|480-424-3593
			//
			// Same three overrides as the Contact Us Today rule above and for
			// the same reasons, only with this component's taxonomy: type
			// 'feature', location 2, title "let's-work-together!". The
			// description comes from getWorkTogetherComponentText rather than
			// getPhoneNumber, because a staff card has neither a
			// [data-click-to-call] row nor a department label to prefix.
			//
			// isTelephonicUrl is what narrows this to the call icon: the same
			// card's email and info actions are anchors too.
			//
			// eVar29 (clickToCallDepartment) is derived per card too — see
			// getWorkTogetherDepartment. DDC publishes no department for a
			// staff-card click, so without that the beacon read `other`.
			//
			// The taxonomy AND the description are overridden. `description`
			// cannot come from the shared helper: it takes the anchor's visible
			// text first, which DDC reports as "Phone Number" for this icon,
			// so it emitted `…|let's-work-together|phone-number` live. See
			// getWorkTogetherComponentText.
			//
			// Unflagged and `unshift`ed for the same reasons as that rule.
			// Left alone, these clicks report the staff-listing widget's own
			// heading as the component title, in the Dealer Information
			// taxonomy — dealer|dealer-information|1|commercial-vehicle-sales.
			//
			// No social rule for this component: it has no social icons.
			// ws-staff-listing renders call, email and info actions only, and
			// the content-default widget above it is the heading alone.
			{
				insertion: 'unshift',
				logic: ({ eventData } = {}) =>
					eventData?.isTelephonicUrl === true &&
					eventData?.widgetName === WORK_TOGETHER_WIDGET_NAME,
				payload: ({ eventData = {}, element } = {}) => {
					// The engine blanks eventData.element to keep the event
					// serializable and passes the node alongside, but read both
					// so the rule does not depend on which it gets.
					const node = element || eventData.element;
					const description = getWorkTogetherComponentText({
						element: node,
						text: eventData.text,
						url: eventData.url
					});
					const department = getWorkTogetherDepartment({
						element: node,
						widgetHeading: eventData.widgetHeading
					});

					return {
						type: 'CustomTagEvent',
						eventName: 'clickToCall',
						eventAction: 'pageElementInteraction',
						attributes: {
							...getGranularClickToCallAttributes(eventData),
							type: 'feature',
							location: 2,
							title: WORK_TOGETHER_COMPONENT_TITLE,
							// Both spreads are conditional so that if a source
							// is missing the beacon keeps the shared helper's
							// value rather than losing the segment entirely.
							...(description && { description }),
							...(department && {
								clickToCallDepartment: department
							})
						}
					};
				}
			}
		],
		// Social Interaction (SDG 5.4.2.7). Unshifted ahead of the generic
		// socialHeaderClick rule, which hardcodes type 'header' and would report
		// title 'n/a' for this headingless content-raw widget.
		'ddc.link.socialHeaderClick': [
			{
				insertion: 'unshift',
				logic: inTaggedWidget(SOCIAL_TAGGING_CLASS),
				payload: ({ eventData = {}, element } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'social',
					eventAction: 'pageElementInteraction',
					attributes: {
						site: 'dealer',
						type: 'dealer-information',
						location: '1',
						title: COMPONENT_TITLE,
						// `|| ''` is load-bearing: a null reaching toAdobeLinkNameSlug throws, which
						// drops the beacon silently.
						description: toAdobeLinkNameSlug(
							getSocialNetwork({ element, eventData }) || ''
						)
					}
				})
			}
		]
	}
};
